import React, { useContext, useEffect, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router";
import type { CfarRateQuoteSuccess } from "@b2bportal/lodging-api";
import {
  GenericDetailsCard,
  GenericDetailsCardComponent,
  GenericDetailsCardComponentEnum,
  GenericDetailsCardTopSectionComponent,
  GenericDetailsCardTopSectionComponentEnum,
  ErrorOutsideComponentType,
  IconName,
  useDeviceTypes,
  getTotalPriceText,
  getIsExpirationDateEqualToCheckIn,
} from "halifax";
import {
  HotelCancellationPolicyV2,
  HotelCancellationPolicyV2Enum,
  CFAR_HEADER,
  HOTEL_CFAR_RESOLUTION,
  HOTEL_CFAR_TERMS_BODY,
  HOTEL_CFAR_TERMS_TABLE,
  Prices,
} from "redmond";
import dayjs from "dayjs";
import clsx from "clsx";
import { CDN_ASSETS, getPortalName } from "@capone/common";
import { ClientContext } from "../../../../../../App";
import { PATH_TERMS_HOTEL_CFAR } from "../../../../../../utils/paths";
import { goToCheckout } from "../../../../../shop/utils/queryStringHelpers";
import { DO_NOT_APPLY_OPTION_KEY } from "../../../../reducer";
import type { StandAloneCfarDetailsConnectorProps } from "./container";
import {
  LOCATION_TEXT,
  CONTINUE_BUTTON_COPY,
  CFAR_DETAILS_SUBTITLE,
  CAP1_HOTEL_CFAR_REFUNDABLE_UPON_CANCELLATION,
  CAP1_HOTEL_CFAR_NONREFUNDABLE_UPON_CANCELLATION,
  NONREFUNDABLE_WARNING_BANNER_COPY,
  CFAR_NO_OPTION_SELECTED_COPY,
  YES_OPTION_COPY,
  NO_OPTION_COPY,
  CFAR_RADIO_BUTTONS_GROUP,
  READ_TERMS_AND_CONDITIONS_COPY,
  COVERAGE_UNDER_100_CFAR_DETAILS,
  GENERIC_WARNING_BANNER_COPY,
  STANDARDIZED_FULLY_REFUNDABLE_CFAR_DETAILS,
  STANDARDIZED_PARTIALLY_REFUNDABLE_CFAR_DETAILS,
} from "./constants";

import "./styles.scss";

export enum CfarOption {
  No,
  Yes,
}

export enum ErrorFromInsideComponentType {
  NoOptionSelected,
}

export interface StandAloneCfarDetailsProps
  extends StandAloneCfarDetailsConnectorProps,
    RouteComponentProps {
  onClose?: () => void;
  // Note: Overwrite the default continue action
  onContinue?: () => void;
  contentOnly?: boolean;
  cardContentProps?: {
    hideTopContent?: boolean;
    hideSubtitle?: boolean;
    hideContinueCta?: boolean;
    disablePartialScroll?: boolean;
    readTermsAndConditionsLinkPosition?: "left" | "center" | "right";
    onSelectValueCallback?: (option: CfarOption) => void;
    onConfirmSelectedValueCallback?: () => void;
    error?: ErrorOutsideComponentType;
  };
  disabled?: boolean;
  contentClassName?: string;
  currentHotelCfarQuote: CfarRateQuoteSuccess;
  currentCancellationPolicy: HotelCancellationPolicyV2 | undefined;
}

export const StandAloneCfarDetails = (props: StandAloneCfarDetailsProps) => {
  const {
    history,
    onClose,
    onContinue = () => {
      goToCheckout({ history });
    },
    currentHotelCfarQuote,
    currentCancellationPolicy,
    selectedCfarId,
    setSelectedCfarId,
    selectedAccountReferenceId,
    contentOnly,
    cardContentProps,
    disabled,
    contentClassName,
    selectedLodging,
    hotelShopDateRange,
  } = props;

  const {
    hideTopContent,
    hideSubtitle,
    hideContinueCta,
    readTermsAndConditionsLinkPosition,
    onSelectValueCallback,
    onConfirmSelectedValueCallback,
    error,
  } = cardContentProps ?? {};
  const { isAgentPortal } = useContext(ClientContext);
  const { matchesDesktop, matchesMobile } = useDeviceTypes();

  const {
    refundPremiumAtExercise: isCfarFeeRefundable,
    expirationInHotelTz: expirationDate,
    coveragePercentage,
  } = currentHotelCfarQuote;

  const isCoverageUnder100Percent = coveragePercentage < 100;

  const [errorFromInsideComponent, setErrorFromInsideComponent] =
    useState<ErrorFromInsideComponentType | null>(null);

  let selectedValue: CfarOption | undefined;

  if (isAgentPortal) {
    selectedValue = CfarOption.No;
  } else if (selectedCfarId !== null) {
    if (selectedCfarId.value !== DO_NOT_APPLY_OPTION_KEY) {
      selectedValue = CfarOption.Yes;
    } else {
      selectedValue = CfarOption.No;
    }
  }

  const isUnselected = selectedValue === undefined;

  const topContent: GenericDetailsCardTopSectionComponent = {
    className: "hotel-cfar-details-header-with-image",
    image: matchesMobile
      ? CDN_ASSETS.capone.cfar.backgroundMobile
      : CDN_ASSETS.capone.cfar.backgroundDesktop,
    location: LOCATION_TEXT,
    header: CFAR_HEADER({
      textVersion: "short",
      punctuation: matchesDesktop ? "." : undefined,
    }),
    component:
      GenericDetailsCardTopSectionComponentEnum.HeaderWithImageBackground,
  };

  const handleViewTerms = () => {
    window.open(PATH_TERMS_HOTEL_CFAR, "_blank")?.focus();
  };

  const mainContent = useMemo((): GenericDetailsCardComponent[] => {
    const mainContentItems: GenericDetailsCardComponent[] = [];
    const continueCta: GenericDetailsCardComponent = {
      message: CONTINUE_BUTTON_COPY,
      onClick: onContinue,
      onClickWhenDisabled: () => {
        if (!selectedValue) {
          setErrorFromInsideComponent(
            ErrorFromInsideComponentType.NoOptionSelected
          );
        }
      },
      ariaLabel: CONTINUE_BUTTON_COPY,
      disabled: isUnselected,
      floating: matchesMobile,
      hidden: matchesMobile && isUnselected,
      fill: "blue",
      component: GenericDetailsCardComponentEnum.GenericCta,
    };

    // Error can be tracked from:
    // 1) The continue button outside of this modal clicked. This is signaled via the error
    //    passed along in the props
    // 2) The continue button of this modal clicked.
    const hasError =
      error === ErrorOutsideComponentType.NoOptionSelected ||
      errorFromInsideComponent ===
        ErrorFromInsideComponentType.NoOptionSelected;

    const radioTitleId = `hotel-cfar-radio-group`;

    if (!hideSubtitle) {
      mainContentItems.push({
        id: radioTitleId,
        className: clsx("hotel-cfar-details-generic-copy", "bold"),
        type: "secondary",
        copy: CFAR_DETAILS_SUBTITLE,
        component: GenericDetailsCardComponentEnum.GenericCopy,
      });
    }

    // Start first bullet point
    const expirationDateString = expirationDate
      ? dayjs(expirationDate).format("MMM D")
      : "";

    const checkInDate = hotelShopDateRange?.from;
    const checkInTime =
      selectedLodging?.lodging.checkInInstructions?.checkInTime;

    const isExpirationDateEqualToCheckIn = getIsExpirationDateEqualToCheckIn(
      checkInDate,
      checkInTime,
      expirationDate
    );

    const quoteCoverageAmountString = currentHotelCfarQuote.coverageAmount.fiat
      ? getTotalPriceText({
          price: currentHotelCfarQuote.coverageAmount.fiat,
        })
      : "";

    const firstBulletPointIcon = IconName.MoneyCounterClockwiseBlue;

    if (isCoverageUnder100Percent) {
      mainContentItems.push({
        className: "hotel-cfar-details-check-mark-icon",
        type: "secondary",
        copy: COVERAGE_UNDER_100_CFAR_DETAILS({
          expirationDateString,
          expirationEqualToCheckIn: isExpirationDateEqualToCheckIn,
          coveragePercentage,
          coverageAmount: quoteCoverageAmountString,
        }),
        icon: firstBulletPointIcon,
        component: GenericDetailsCardComponentEnum.GenericCopy,
      });
    } else {
      const firstBulletPointCopy = isCfarFeeRefundable
        ? STANDARDIZED_FULLY_REFUNDABLE_CFAR_DETAILS({
            expirationDateString,
            expirationEqualToCheckIn: isExpirationDateEqualToCheckIn,
            coverageAmount: quoteCoverageAmountString,
          })
        : STANDARDIZED_PARTIALLY_REFUNDABLE_CFAR_DETAILS({
            expirationDateString,
            expirationEqualToCheckIn: isExpirationDateEqualToCheckIn,
            coverageAmount: quoteCoverageAmountString,
          });

      mainContentItems.push({
        className: clsx("hotel-cfar-details-check-mark-icon", {
          fade: disabled,
        }),
        type: "secondary",
        copy: firstBulletPointCopy,
        icon: firstBulletPointIcon,
        component: GenericDetailsCardComponentEnum.GenericCopy,
      });
    }

    // End first bullet point

    const secondBulletPointCopy = HOTEL_CFAR_RESOLUTION(getPortalName());
    const secondBulletPointIcon = IconName.SimpleLaptopBlue;

    mainContentItems.push({
      className: clsx("hotel-cfar-details-check-mark-icon", "with-money-icon", {
        fade: disabled,
      }),
      type: "secondary",
      copy: secondBulletPointCopy,
      icon: secondBulletPointIcon,
      component: GenericDetailsCardComponentEnum.GenericCopy,
    });
    if (!isCoverageUnder100Percent) {
      const thirdBulletPointCopy = isCfarFeeRefundable
        ? CAP1_HOTEL_CFAR_REFUNDABLE_UPON_CANCELLATION(
            currentHotelCfarQuote.premiumAmount.fiat,
            false
          )
        : CAP1_HOTEL_CFAR_NONREFUNDABLE_UPON_CANCELLATION(
            currentHotelCfarQuote.premiumAmount.fiat,
            false
          );
      const thirdBulletPointIcon = isCfarFeeRefundable
        ? IconName.CheckCircleBorder
        : IconName.NotAllowedSignBlue;

      mainContentItems.push({
        className: clsx("hotel-cfar-details-check-mark-icon", {
          fade: disabled,
        }),
        type: "secondary",
        copy: thirdBulletPointCopy,
        icon: thirdBulletPointIcon,
        component: GenericDetailsCardComponentEnum.GenericCopy,
      });
    }

    const hasKnownCancellationPolicy =
      currentCancellationPolicy &&
      currentCancellationPolicy.CancellationPolicyV2 !==
        HotelCancellationPolicyV2Enum.Unknown;

    let copy = GENERIC_WARNING_BANNER_COPY;

    if (hasKnownCancellationPolicy) {
      const isNonRefundable =
        currentCancellationPolicy.CancellationPolicyV2 ===
        HotelCancellationPolicyV2Enum.NonRefundable;

      if (isNonRefundable) {
        copy = NONREFUNDABLE_WARNING_BANNER_COPY;
      } else if (currentCancellationPolicy.cfarInfoBannerText != null) {
        copy = currentCancellationPolicy.cfarInfoBannerText;
      }
    }

    mainContentItems.push({
      copy,
      fill: "orange",
      size: "small",
      hideLeftIcon: true,
      hideRightIcon: true,
      useLeftBorder: true,
      component: GenericDetailsCardComponentEnum.NotificationBanner,
    });

    if (hasError) {
      mainContentItems.push({
        className: "hotel-cfar-details-error-message",
        type: "error",
        copy: CFAR_NO_OPTION_SELECTED_COPY,
        component: GenericDetailsCardComponentEnum.GenericCopy,
        newSection: true,
      });
    }

    mainContentItems.push({
      radioButtons: [
        {
          value: CfarOption.Yes,
          copy: YES_OPTION_COPY(
            currentHotelCfarQuote.premiumAmount.fiat,
            selectedAccountReferenceId
              ? (currentHotelCfarQuote.premiumAmount as Prices).rewards[
                  selectedAccountReferenceId
                ]
              : undefined
          ),
          disabled: isAgentPortal,
          dataTestId: "cfar-yes-radio",
        },
        {
          value: CfarOption.No,
          copy: NO_OPTION_COPY,
          dataTestId: "cfar-no-radio",
        },
      ],
      selectedValue,
      setSelectedValue: (value: number) => {
        switch (value) {
          case CfarOption.Yes:
            if (!isAgentPortal) {
              setSelectedCfarId({ value: currentHotelCfarQuote.id });
            }
            break;
          case CfarOption.No:
            setSelectedCfarId({
              value: DO_NOT_APPLY_OPTION_KEY,
            });
            break;
          default:
            break;
        }

        setErrorFromInsideComponent(null);

        if (onSelectValueCallback) {
          onSelectValueCallback(value);
        }
      },
      /*
        note: collapse only after activation to allow keyboard users to still hear
        the option selected and possibly navigate to other options.
      */
      onConfirmSelectedValue: () => {
        if (onConfirmSelectedValueCallback) {
          onConfirmSelectedValueCallback();
        }
      },
      hasError,
      radioGroupName: CFAR_RADIO_BUTTONS_GROUP,
      ariaLabelledBy: radioTitleId,
      disabled,
      component: GenericDetailsCardComponentEnum.BoxedRadioGroup,
    });

    if (matchesMobile) {
      mainContentItems.push(continueCta);

      mainContentItems.push({
        content: [
          {
            title: READ_TERMS_AND_CONDITIONS_COPY,
            body:
              HOTEL_CFAR_TERMS_BODY(getPortalName()) + HOTEL_CFAR_TERMS_TABLE,
          },
        ],
        component: GenericDetailsCardComponentEnum.AccordionGroup,
      });
    }

    mainContentItems.push({
      message: READ_TERMS_AND_CONDITIONS_COPY,
      onClick: handleViewTerms,
      position: readTermsAndConditionsLinkPosition ?? "center",
      disabled,
      component: GenericDetailsCardComponentEnum.ClickableLink,
    });

    return mainContentItems;
  }, [
    currentHotelCfarQuote,
    selectedAccountReferenceId,
    isAgentPortal,
    selectedValue,
    onContinue,
    isUnselected,
    matchesMobile,
    hideContinueCta,
    readTermsAndConditionsLinkPosition,
    onSelectValueCallback,
    onConfirmSelectedValueCallback,
    error,
  ]);

  useEffect(() => {
    // note: scroll to top upon arriving at this screen on mobile.
    if (matchesMobile) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  }, [matchesMobile]);

  useEffect(() => {
    if (isAgentPortal) {
      setSelectedCfarId({
        value: DO_NOT_APPLY_OPTION_KEY,
      });
    }
  }, [isAgentPortal]);

  return (
    <GenericDetailsCard
      popupClassName="hotel-hotel-cfar-generic-details-card"
      contentClassName={contentClassName}
      topContent={!hideTopContent ? topContent : undefined}
      mainContent={mainContent}
      openModal={matchesMobile}
      onClose={onClose}
      isMobile={matchesMobile}
      contentOnly={contentOnly ?? matchesMobile}
      scrollOption="default-scroll"
    />
  );
};
